import { cptThumb } from 'commons/cdn'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { filterIt } from 'CanvasCore/utils'
import { STICKER_SIZE } from './geometry'
import { anchorKind, componentPoints, resolveComponentAnchors } from './componentAnchors'

/** 五官组。眉毛、眼眶、眼白、瞳孔、高光、鼻子、嘴巴。 */
const FACE_GROUP_ID = 'LOLWJIAI9Tj7Gr4Efui0'

type LayerRef = { id?: string; gid?: string; name?: string; cover?: string }
type DrawerRef = {
  cid?: string
  lid?: string
  x?: number
  y?: number
  w?: number
  h?: number
  z?: number
  t?: string
}

const imageCache = new Map<string, Promise<HTMLImageElement>>()

const loadImage = (url: string) => {
  const cached = imageCache.get(url)
  if (cached) return cached
  const pending = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => {
      imageCache.delete(url)
      reject(new Error('五官组件加载失败'))
    }
    image.src = url
  })
  imageCache.set(url, pending)
  return pending
}

export function faceDrawKey(
  layers: LayerRef[],
  drawers: DrawerRef[] | undefined,
  tonings: Record<string, { default?: { color?: unknown } }> | undefined,
) {
  const faceLayerIds = new Set(
    layers
      .filter((layer) => layer.id === FACE_GROUP_ID || layer.gid === FACE_GROUP_ID)
      .map((layer) => layer.id),
  )
  const layerById = new Map(layers.map(layer => [layer.id, layer]))
  return (drawers ?? [])
    .filter((drawer) => drawer.lid && faceLayerIds.has(drawer.lid) && drawer.cid)
    .map((drawer) => {
      const color = drawer.t ? tonings?.[drawer.t]?.default?.color : ''
      const colorKey = typeof color === 'string' ? color : color ? JSON.stringify(color) : ''
      const layer = layerById.get(drawer.lid)
      return [drawer.lid, drawer.cid, drawer.x, drawer.y, drawer.w, drawer.h, drawer.z, colorKey, layer?.name, layer?.cover].join(':')
    })
    .join('|')
}

/** 把当前画布上的五官画成 512×512，不含身体和头发。 */
export async function renderEditorFace(tid: string, layers: LayerRef[]) {
  const editor = useEditorStore.getState().editors?.[tid]
  const drawers = ((editor?.drawers ?? []) as DrawerRef[]).map(drawer => ({ ...drawer }))
  const tonings = editor?.tonings ?? {}
  const layerById = new Map(layers.map(layer => [layer.id, { ...layer }]))
  const faceLayerIds = new Set(
    layers
      .filter((layer) => layer.id === FACE_GROUP_ID || layer.gid === FACE_GROUP_ID)
      .map((layer) => layer.id)
      .filter((id): id is string => !!id),
  )
  const list = drawers
    .filter((drawer) => drawer.lid && faceLayerIds.has(drawer.lid) && drawer.cid)
    .sort((a, b) => (a.z ?? 0) - (b.z ?? 0))
  if (!list.length) throw new Error('当前画布的五官层上没有组件')

  const canvas = document.createElement('canvas')
  canvas.width = STICKER_SIZE
  canvas.height = STICKER_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('无法绘制五官')

  const colors = new Map(list.map(drawer => [drawer, drawer.t ? tonings[drawer.t]?.default?.color : undefined]))
  const candidates: { kind: NonNullable<ReturnType<typeof anchorKind>>; points: { x: number; y: number }[] }[] = []
  for (const drawer of list) {
    const image = await loadImage(cptThumb(drawer.cid))
    const w = drawer.w ?? STICKER_SIZE
    const h = drawer.h ?? STICKER_SIZE
    const part = document.createElement('canvas')
    part.width = Math.max(1, Math.round(w))
    part.height = Math.max(1, Math.round(h))
    const partCtx = part.getContext('2d', { willReadFrequently: true })
    if (!partCtx) continue
    partCtx.drawImage(image, 0, 0, part.width, part.height)
    const color = colors.get(drawer)
    if (color) {
      const imageData = partCtx.getImageData(0, 0, part.width, part.height)
      filterIt(color, imageData)
      partCtx.putImageData(imageData, 0, 0)
    }
    const x = drawer.x ?? 0
    const y = drawer.y ?? 0
    const kind = anchorKind(layerById.get(drawer.lid))
    if (kind) {
      const pixels = partCtx.getImageData(0, 0, part.width, part.height)
      const points = componentPoints(pixels.data, part.width, part.height, kind !== 'mouth')
        .map(point => ({ x: point.x + x, y: point.y + y }))
      candidates.push({ kind, points })
    }
    ctx.drawImage(part, x, y)
  }
  return { face: canvas, anchors: resolveComponentAnchors(candidates) }
}
